import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings, Percent, DoorOpen, Save, Lock } from 'lucide-react';

const categories = [
  { key: 'fundador', label: 'Fundador' },
  { key: 'normal', label: 'Normal' },
  { key: 'frecuente', label: 'Frecuente' },
  { key: 'eventos', label: 'Eventos' },
];

export default function SettingsPage() {
  const { API } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(() => {
    axios.get(`${API}/settings`).then(r => { setSettings(r.data); setLoading(false); }).catch(() => { setLoading(false); toast.error('Error al cargar configuracion'); });
  }, [API]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings`, {
        category_discounts: settings.category_discounts,
        entry_fees: settings.entry_fees,
        total_lockers: settings.total_lockers || 50,
        lockers_per_row: settings.lockers_per_row || 10
      });
      setSettings(data);
      toast.success('Configuracion guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  const updateDiscount = (cat, value) => {
    setSettings(prev => ({ ...prev, category_discounts: { ...prev.category_discounts, [cat]: parseFloat(value) || 0 } }));
  };

  const updateFee = (cat, value) => {
    setSettings(prev => ({ ...prev, entry_fees: { ...prev.entry_fees, [cat]: parseFloat(value) || 0 } }));
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Cargando...</div>;
  if (!settings) return <div className="text-stone-500">Error al cargar configuracion</div>;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Configuracion</h1>
          <p className="text-stone-500 text-sm mt-1">Descuentos por categoria y cuotas de entrada</p>
        </div>
        <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving}
          className="bg-orange-700 hover:bg-orange-800 text-white rounded-full px-6 shadow-sm hover:shadow-md active:scale-[0.98] transition-all">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <Percent className="w-5 h-5 text-purple-700" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-outfit text-lg font-medium text-stone-900">Descuentos por Categoria</h2>
                <p className="text-xs text-stone-500">Porcentaje de descuento aplicado al cobro</p>
              </div>
            </div>
            <div className="space-y-4">
              {categories.map(cat => (
                <div key={cat.key} className="flex items-center justify-between gap-4">
                  <Label className="text-sm font-medium text-stone-700 min-w-[100px]">{cat.label}</Label>
                  <div className="relative w-32">
                    <Input
                      data-testid={`discount-${cat.key}`}
                      type="number" min="0" max="100" step="1"
                      value={settings.category_discounts?.[cat.key] ?? 0}
                      onChange={e => updateDiscount(cat.key, e.target.value)}
                      className="rounded-xl pr-8 text-right"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                <DoorOpen className="w-5 h-5 text-orange-700" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-outfit text-lg font-medium text-stone-900">Cuota de Entrada</h2>
                <p className="text-xs text-stone-500">Se agrega automaticamente al registrar entrada</p>
              </div>
            </div>
            <div className="space-y-4">
              {categories.map(cat => (
                <div key={cat.key} className="flex items-center justify-between gap-4">
                  <Label className="text-sm font-medium text-stone-700 min-w-[100px]">{cat.label}</Label>
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                    <Input
                      data-testid={`entry-fee-${cat.key}`}
                      type="number" min="0" step="10"
                      value={settings.entry_fees?.[cat.key] ?? 0}
                      onChange={e => updateFee(cat.key, e.target.value)}
                      className="rounded-xl pl-8 text-right"
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-outfit text-lg font-medium text-stone-900">Lockers</h2>
                <p className="text-xs text-stone-500">Numero total de lockers disponibles en el club</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <Label className="text-sm font-medium text-stone-700">Total de lockers</Label>
              <Input
                data-testid="total-lockers"
                type="number" min="1" max="500"
                value={settings.total_lockers ?? 50}
                onChange={e => setSettings(prev => ({ ...prev, total_lockers: parseInt(e.target.value) || 50 }))}
                className="rounded-xl w-32 text-right"
              />
            </div>
            <div className="flex items-center justify-between gap-4 mt-4">
              <Label className="text-sm font-medium text-stone-700">Lockers por fila</Label>
              <Input
                data-testid="lockers-per-row"
                type="number" min="1" max="20"
                value={settings.lockers_per_row ?? 10}
                onChange={e => setSettings(prev => ({ ...prev, lockers_per_row: parseInt(e.target.value) || 10 }))}
                className="rounded-xl w-32 text-right"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-stone-50 border border-stone-200 rounded-2xl">
        <CardContent className="p-6">
          <h3 className="font-outfit font-medium text-stone-800 mb-2">Como funcionan</h3>
          <ul className="text-sm text-stone-600 space-y-1.5 list-disc list-inside">
            <li><strong>Descuentos:</strong> Se aplican automaticamente al total del consumo cuando se cobra la visita.</li>
            <li><strong>Cuota de Entrada:</strong> Se agrega como primer item al registrar la entrada del cliente.</li>
            <li>Los clientes <strong>Fundador</strong> tipicamente tienen cuota $0 y mayor descuento como beneficio.</li>
            <li>Puedes poner $0 en la cuota para categorias que no pagan entrada.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
